import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { ensureCategories, withRenderValue } from './pieDataUtils';
import { useDashboardSplit } from '../hooks/useDashboardSplit';
import { DASH_TOOLTIP } from './tooltipStyle';

// Slice fills rotate the chart palette by index mod 6.
const chartColor = (i) => `var(--dash-chart-${(i % 6) + 1})`;

/**
 * Shared donut for "Vendor Split over X" widgets: center total in the hole,
 * legend as a name/count/% table on the right. Square plot sized by `height`.
 */
const DonutSplitChart = ({
  title,
  endpoint,
  params,
  fixedCategories,
  centerLabel = 'VENDORS',
  // When true, the descriptive label is rendered below the donut instead of
  // inside the hole — use for longer, multi-word labels that would otherwise
  // overflow the ring.
  labelBelow = false,
  cornerLabel,
  height = 140,
  transform,
}) => {
  const { data, loading } = useDashboardSplit(endpoint, params);

  // Like BarSplitChart, this card lands in containers of very different widths.
  // The donut is a fixed square, so in a narrow column the side legend is what
  // gets crushed — and flex rows overflow instead of shrinking, spilling legend
  // text across whatever sits to the right. Below ~260px, stack the legend
  // under the donut instead of beside it.
  const rootRef = React.useRef(null);
  const [rootWidth, setRootWidth] = React.useState(0);
  React.useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => setRootWidth(entry.contentRect.width));
    ro.observe(el);
    setRootWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);
  const stacked = rootWidth > 0 && rootWidth < 260;
  const donutSize = stacked ? Math.min(height, 110) : height;

  const normalized = useMemo(() => {
    const base = Array.isArray(data) ? data : [];
    const filtered = transform ? transform(base) : base;
    const shaped = fixedCategories ? ensureCategories(filtered, fixedCategories) : filtered;
    return shaped.map((d, i) => ({ ...d, color: chartColor(i) }));
  }, [data, fixedCategories, transform]);

  const pie = useMemo(() => withRenderValue(normalized), [normalized]);
  const isEmpty = !loading && (!Array.isArray(data) || data.length === 0);
  const total = Number(pie.total) || 0;

  const legendRows = useMemo(() => {
    const positives = pie.data.filter((d) => (Number(d.value) || 0) > 0);
    return positives.length > 0 ? positives : pie.data;
  }, [pie.data]);

  const tooltipFormatter = (value, name, entry) => {
    const real = Number(entry?.payload?.value) || 0;
    const pct = total > 0 ? ((real / total) * 100).toFixed(1) : '0.0';
    return [`${real.toLocaleString()} · ${pct}%`, name];
  };

  return (
    <div ref={rootRef} className="flex flex-col h-full min-h-[200px] overflow-hidden">
      <div className="flex items-center justify-between mb-2 flex-shrink-0">
        <h3 className="text-sm font-semibold text-dash-foreground">{title}</h3>
        {cornerLabel ? <span className="text-[11px] text-dash-muted-foreground">{cornerLabel}</span> : null}
      </div>
      {isEmpty ? (
        <div className="flex-1 flex items-center justify-center text-[11px] text-dash-muted-foreground min-h-[150px]">
          No data available
        </div>
      ) : (
        <div className={stacked ? 'flex flex-col items-center gap-2 flex-1 min-w-0' : 'flex items-center gap-3 flex-1 min-w-0'}>
          <div className="flex-shrink-0 flex flex-col items-center gap-1">
          <div className="relative" style={{ width: donutSize, height: donutSize }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pie.data}
                  cx="50%"
                  cy="50%"
                  innerRadius="62%"
                  outerRadius="92%"
                  paddingAngle={1.5}
                  dataKey="renderValue"
                  stroke="var(--dash-card)"
                  strokeWidth={1}
                >
                  {pie.data.map((entry, idx) => (<Cell key={idx} fill={entry.color} />))}
                </Pie>
                <Tooltip
                  formatter={tooltipFormatter}
                  contentStyle={DASH_TOOLTIP.contentStyle}
                  labelStyle={DASH_TOOLTIP.labelStyle}
                  itemStyle={DASH_TOOLTIP.itemStyle}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-lg font-semibold tabular-nums leading-none text-dash-foreground">
                {total.toLocaleString()}
              </span>
              {!labelBelow && (
                <span className="text-[10px] uppercase tracking-wider text-dash-muted-foreground mt-1">
                  {centerLabel}
                </span>
              )}
            </div>
          </div>
          {labelBelow && (
            <span className="text-[10px] uppercase tracking-wider text-dash-muted-foreground text-center leading-tight">
              {centerLabel}
            </span>
          )}
          </div>
          <div className={`${stacked ? 'w-full' : 'flex-1'} min-w-0 text-[11px] space-y-1`}>
            {legendRows.map((entry, idx) => {
              const value = Number(entry.value) || 0;
              const percent = total > 0 ? (value / total) * 100 : 0;
              return (
                <div key={idx} className="flex items-center gap-2">
                  <span className="size-2 rounded-[2px] flex-shrink-0" style={{ backgroundColor: entry.color }} />
                  <span className="flex-1 truncate text-dash-muted-foreground">{entry.name}</span>
                  <span className="font-medium tabular-nums text-dash-foreground">{value.toLocaleString()}</span>
                  <span className="w-10 text-right tabular-nums text-dash-muted-foreground">{percent.toFixed(1)}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default DonutSplitChart;
